import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
} from "flowbite-react";
import {
  FaMagnifyingGlass,
  FaSort,
  FaSortUp,
  FaSortDown,
} from "react-icons/fa6";
import ChangeUserPassword from "./ChangeUserPassword";
import EditUserModal from "./EditUserModal";
import type { UserSortField, SortOrder } from "./list";
import { UserSortField as UserSortFieldEnum } from "./list";
import type { ColumnConfig } from "./ColumnSelector";

const AllUsersTable = function (props: {
  users: { data: any[]; total: number };
  getUsers: () => void;
  sortField: UserSortField | null;
  sortOrder: SortOrder;
  onSort: (columnName: UserSortField) => void;
  visibleColumns: ColumnConfig[];
}) {
  const handleSort = (columnName: UserSortField) => {
    props.onSort(columnName);
  };

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(new Date(dateString));
  };

  if (props.users?.data?.length === 0) {
    return (
      <div className="flex h-84 w-full items-center justify-center">
        <div className="flex size-40 flex-col items-center justify-center gap-3 rounded-full  bg-gray-100 p-4 text-gray-400">
          <FaMagnifyingGlass size={45} />
          <span className="text-sm font-normal">No users found</span>
        </div>
      </div>
    );
  }

  const isColumnVisible = (columnId: UserSortField | "actions") => {
    return (
      props.visibleColumns.find((col) => col.id === columnId)?.enabled ?? true
    );
  };

  const SortableHeadCell = ({
    columnName,
    children,
  }: {
    columnName: UserSortField;
    children: React.ReactNode;
  }) => {
    if (!isColumnVisible(columnName)) return null;

    return (
      <TableHeadCell
        onClick={() => handleSort(columnName)}
        className="cursor-pointer"
      >
        <div className="flex items-center">
          {children}
          {props.sortField === columnName ? (
            props.sortOrder === "asc" ? (
              <FaSortUp className="ml-2" />
            ) : (
              <FaSortDown className="ml-2" />
            )
          ) : (
            <FaSort className="ml-2 text-gray-400" />
          )}
        </div>
      </TableHeadCell>
    );
  };

  return (
    <Table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
      <TableHead className="bg-gray-100 dark:bg-gray-700">
        <SortableHeadCell columnName={UserSortFieldEnum.NAME}>
          User
        </SortableHeadCell>
        <SortableHeadCell columnName={UserSortFieldEnum.CREATED_AT}>
          Creation Date
        </SortableHeadCell>
        <SortableHeadCell columnName={UserSortFieldEnum.ACTIVITY_STATUS}>
          Activity Status
        </SortableHeadCell>
        <SortableHeadCell columnName={UserSortFieldEnum.IDENTITY_PROVIDER}>
          Auth Provider
        </SortableHeadCell>
        <SortableHeadCell
          columnName={UserSortFieldEnum.DAILY_AVERAGE_MINUTES_PLAYED}
        >
          Daily Average Played
        </SortableHeadCell>
        <SortableHeadCell columnName={UserSortFieldEnum.TOTAL_MINUTES_PLAYED}>
          Total Time Played
        </SortableHeadCell>
        <SortableHeadCell columnName={UserSortFieldEnum.LAST_LOGIN_TIMESTAMP}>
          Last Login
        </SortableHeadCell>
        <SortableHeadCell columnName={UserSortFieldEnum.STATUS}>
          Status
        </SortableHeadCell>
        <SortableHeadCell columnName={UserSortFieldEnum.STREAK_DAYS}>
          Streak Days
        </SortableHeadCell>
        <SortableHeadCell columnName={UserSortFieldEnum.BEST_STREAK}>
          Best Streak
        </SortableHeadCell>
        {isColumnVisible("actions") && <TableHeadCell>Actions</TableHeadCell>}
      </TableHead>
      <TableBody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
        {props.users?.data?.map((user: any, index: number) => {
          return (
            <TableRow
              key={index}
              className="hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              {isColumnVisible(UserSortFieldEnum.NAME) && (
                <TableCell className="whitespace-nowrap px-6 py-4">
                  <div className="text-sm font-normal text-gray-500 dark:text-gray-400">
                    <div className="text-base font-semibold text-gray-900 dark:text-white">
                      {user?.name || "Unknown"}
                    </div>
                    <div className="text-sm font-normal text-gray-500 dark:text-gray-400">
                      {user.email}
                    </div>
                  </div>
                </TableCell>
              )}
              {isColumnVisible(UserSortFieldEnum.CREATED_AT) && (
                <TableCell className="whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                  {formatDate(user.created_at as string)}
                </TableCell>
              )}
              {isColumnVisible(UserSortFieldEnum.ACTIVITY_STATUS) && (
                <TableCell>
                  <div className="text-sm font-medium text-gray-900 dark:text-white text-center">
                    {user.activity_status !== null ? user.activity_status : "-"}
                  </div>
                </TableCell>
              )}
              {isColumnVisible(UserSortFieldEnum.IDENTITY_PROVIDER) && (
                <TableCell className="whitespace-nowrap text-sm font-medium capitalize text-gray-900 dark:text-white">
                  {user.identity_provider}
                </TableCell>
              )}
              {isColumnVisible(
                UserSortFieldEnum.DAILY_AVERAGE_MINUTES_PLAYED,
              ) && (
                <TableCell>
                  <div className="text-sm font-medium text-gray-900 dark:text-white text-center">
                    {user.daily_average_minutes_played !== null
                      ? user.daily_average_minutes_played
                      : "-"}
                  </div>
                </TableCell>
              )}
              {isColumnVisible(UserSortFieldEnum.TOTAL_MINUTES_PLAYED) && (
                <TableCell>
                  <div className="text-sm font-medium text-gray-900 dark:text-white text-center">
                    {user.total_minutes_played !== null
                      ? user.total_minutes_played
                      : "-"}
                  </div>
                </TableCell>
              )}
              {isColumnVisible(UserSortFieldEnum.LAST_LOGIN_TIMESTAMP) && (
                <TableCell>
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {user.last_login_timestamp &&
                      Intl.DateTimeFormat("en-US", {
                        year: "numeric",
                        month: "numeric",
                        day: "numeric",
                        hour: "numeric",
                        minute: "numeric",
                      }).format(new Date(user.last_login_timestamp))}
                  </div>
                </TableCell>
              )}
              {isColumnVisible(UserSortFieldEnum.STATUS) && (
                <TableCell className="whitespace-nowrap px-6 py-4 text-sm font-normal text-gray-900 dark:text-white">
                  <div className="flex items-center">
                    <div
                      className={`mr-2 size-2.5 rounded-full ${
                        user.status === "STATUS_ACTIVE"
                          ? "bg-green-400"
                          : "bg-red-400"
                      }`}
                    />
                    {user.status === "STATUS_ACTIVE" ? "Active" : "Not Active"}
                  </div>
                </TableCell>
              )}
              {isColumnVisible(UserSortFieldEnum.STREAK_DAYS) && (
                <TableCell>
                  <div className="text-sm font-medium text-gray-900 dark:text-white text-center">
                    {user.streak_days !== null && user.streak_days !== undefined
                      ? user.streak_days
                      : "-"}
                  </div>
                </TableCell>
              )}
              {isColumnVisible(UserSortFieldEnum.BEST_STREAK) && (
                <TableCell>
                  <div className="text-sm font-medium text-gray-900 dark:text-white text-center">
                    {user.best_streak !== null && user.best_streak !== undefined
                      ? user.best_streak
                      : "-"}
                  </div>
                </TableCell>
              )}
              {isColumnVisible("actions") && (
                <TableCell>
                  <div className="flex items-center gap-x-3 whitespace-nowrap">
                    <EditUserModal user={user} getUsers={props.getUsers} />
                    <ChangeUserPassword user={user} getUsers={props.getUsers} />
                  </div>
                </TableCell>
              )}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default AllUsersTable;
